import { useState } from 'react';
import { useTranslation } from '../../i18n/I18nProvider';
import { toStartsAt } from '../../lib/calendarGrid';
import { Button, Input, Modal, Select } from '../../ui';

/** Podsjetnik prije termina. `null` gasi podsjetnik; 60 minuta je zadano. */
export const REMINDER_OPTIONS: readonly (number | null)[] = [null, 15, 30, 60, 120];
export const DEFAULT_REMINDER_MINUTES = 60;

export interface PlanWorkoutValues {
  startsAt: string;
  workoutId: string | null;
  title: string | null;
  remindMinutesBefore: number | null;
}

export interface PlanWorkoutModalProps {
  open: boolean;
  onClose: () => void;
  /** Uređivanje postojećeg termina mijenja samo vrijeme i podsjetnik. */
  mode: 'create' | 'edit';
  initialDay: string;
  initialTime?: string;
  initialWorkoutId?: string | null;
  initialTitle?: string | null;
  initialReminder?: number | null;
  workouts: readonly { id: string; title: string }[];
  loading?: boolean;
  onSubmit: (values: PlanWorkoutValues) => void;
}

/**
 * Klijent sam upisuje kad će trenirati. Odvojena polja za datum i vrijeme
 * (a ne `datetime-local`) jer je na mobitelu to jedini unos koji ne traži
 * tipkanje formata.
 */
export function PlanWorkoutModal({
  open,
  onClose,
  mode,
  initialDay,
  initialTime = '18:00',
  initialWorkoutId = null,
  initialTitle = null,
  initialReminder = DEFAULT_REMINDER_MINUTES,
  workouts,
  loading = false,
  onSubmit,
}: PlanWorkoutModalProps) {
  const { t } = useTranslation();
  const [day, setDay] = useState(initialDay);
  const [time, setTime] = useState(initialTime);
  const [workoutId, setWorkoutId] = useState<string>(initialWorkoutId ?? '');
  const [title, setTitle] = useState(initialTitle ?? '');
  const [reminder, setReminder] = useState<number | null>(initialReminder);
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(): void {
    const startsAt = toStartsAt(day, time);
    if (!startsAt) {
      setError(t('cal.dateRequired'));
      return;
    }
    if (new Date(startsAt).getTime() < Date.now()) {
      setError(t('cal.planPast'));
      return;
    }
    setError(undefined);
    onSubmit({
      startsAt,
      workoutId: workoutId || null,
      title: title.trim() || null,
      remindMinutesBefore: reminder,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'edit' ? t('cal.rescheduleTitle') : t('cal.planWorkout')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={loading} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-ink-muted">{t('cal.planWorkoutHelp')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            id="plan-day"
            type="date"
            label={t('cal.planDate')}
            value={day}
            error={error}
            onChange={(e) => {
              setDay(e.target.value);
              setError(undefined);
            }}
          />
          <Input
            id="plan-time"
            type="time"
            label={t('cal.planTime')}
            value={time}
            onChange={(e) => {
              setTime(e.target.value);
              setError(undefined);
            }}
          />
        </div>

        {mode === 'create' && (
          <>
            <Input
              id="plan-title"
              label={t('cal.planTitle')}
              placeholder={t('cal.planTitlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Select
              id="plan-workout"
              label={t('cal.linkWorkout')}
              value={workoutId}
              onChange={(e) => setWorkoutId(e.target.value)}
              options={[
                { value: '', label: t('cal.noLinkedWorkout') },
                ...workouts.map((w) => ({ value: w.id, label: w.title })),
              ]}
            />
          </>
        )}

        <Select
          id="plan-reminder"
          label={t('cal.reminder')}
          value={reminder === null ? 'none' : String(reminder)}
          onChange={(e) => setReminder(e.target.value === 'none' ? null : Number(e.target.value))}
          options={REMINDER_OPTIONS.map((minutes) => ({
            value: minutes === null ? 'none' : String(minutes),
            label: minutes === null ? t('cal.reminderNone') : t('cal.reminderMinutes', { n: minutes }),
          }))}
        />
      </div>
    </Modal>
  );
}
